// 솟다 포맷 유틸 — Codex 보강: Intl.NumberFormat('ko-KR') 기반
// 모든 경계값(0, 음수, NaN, Infinity, 거대 수)에 안전

const nfWon = new Intl.NumberFormat("ko-KR", {
  style: "currency",
  currency: "KRW",
  maximumFractionDigits: 0,
});

// 한국식 축약 원화 표기 (2026-07-24 효신님 결정 — 구 표기 ₩50.4M/₩7K는 폐기).
//   5,040만원 · 2억 5,000만원 · 7,200원
// K/M은 영어권 단위라 국내 사용자에게 한 번 더 환산이 필요했다. 만·억 단위로 읽는 그대로 쓴다.
const nfWonShort = (won: number): string => {
  if (!Number.isFinite(won)) return "-원";
  const sign = won < 0 ? "-" : "";
  // 만 단위 절삭 — 1만 이상 금액에서 천 단위 이하는 표기하지 않는다(910만원, 5,040만원).
  // ⚠ 반올림이 아니라 내림인 이유: 99,999,999원이 "1억원"으로 올라가면 자산이 실제보다
  //    커 보인다. 금액 표기는 과대 쪽으로 틀리지 않게 한다.
  const abs = Math.abs(won);
  if (abs < 1e4) return `${sign}${Math.round(abs).toLocaleString("ko-KR")}원`;

  const man = Math.floor(abs / 1e4); // 만 단위 정수
  const eok = Math.floor(man / 1e4); // 억
  const restMan = man % 1e4;         // 억을 뺀 나머지 만

  if (eok > 0) {
    return restMan === 0
      ? `${sign}${eok.toLocaleString("ko-KR")}억원`
      : `${sign}${eok.toLocaleString("ko-KR")}억 ${restMan.toLocaleString("ko-KR")}만원`;
  }
  return `${sign}${man.toLocaleString("ko-KR")}만원`;
};

/** 정확한 ₩50,400,000 표기 (긴 형식) */
export function formatKRW(won: number): string {
  if (!Number.isFinite(won)) return "₩-";
  return nfWon.format(won);
}

/** 한국식 축약 표기 5,040만원 / 2억 5,000만원 / 7,200원 — Hero·KPI용 */
export function formatKRWShort(won: number): string {
  return nfWonShort(won);
}

/** $1,234.56 표기 — 증권 USD 거래 표시용 (Phase 1.9, 2026-05-26) */
export function formatUSD(usd: number): string {
  if (!Number.isFinite(usd)) return "$-";
  const abs = Math.abs(usd);
  const sign = usd < 0 ? "-" : "";
  // 1달러 미만 (동전모으기 매수 $0.6224 등)은 소수 4자리 표시
  if (abs < 1) return `${sign}$${abs.toFixed(4)}`;
  // 100달러 미만은 소수 2자리
  if (abs < 100) return `${sign}$${abs.toFixed(2)}`;
  // 그 이상은 콤마 + 소수 2자리
  return `${sign}$${abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** $50.4K / $2.5M — 축약 표시 */
export function formatUSDShort(usd: number): string {
  if (!Number.isFinite(usd)) return "$-";
  const abs = Math.abs(usd);
  const sign = usd < 0 ? "-" : "";
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return `${sign}$${abs.toFixed(2)}`;
}

/** "+1.2%" / "-0.4%" / "±0%" — 색상은 호출자가 결정 */
export function formatPct(pct: number, opts?: { showZero?: boolean; digits?: number }): string {
  if (!Number.isFinite(pct)) return "-";
  const d = opts?.digits ?? 1;
  if (pct === 0) {
    if (opts?.showZero) return "±0%";
    // digits를 **명시한** 호출자는 0에서도 그 자릿수를 유지한다 (2026-08-02 format 통일 결정).
    //   시세·수익률 표는 "0%"만 짧아지면 자릿수가 흔들려 열 정렬이 깨진다.
    //   digits 미지정(기본 1자리) 호출자는 기존 "0%"를 그대로 — 홈·자산 화면 회귀 방지.
    return opts?.digits != null ? `${(0).toFixed(d)}%` : "0%";
  }
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(d)}%`;
}

/** "+₩620K · +1.2% 1주" 형식 (Hero diff) */
export function formatDelta(
  amount: number,
  pct: number,
  period?: string
): string {
  const a = amount > 0 ? `+${formatKRWShort(amount)}` : formatKRWShort(amount);
  const p = formatPct(pct);
  return period ? `${a} · ${p} ${period}` : `${a} · ${p}`;
}

/** 색상 분기용 tone 판정 */
export function deltaTone(n: number): "positive" | "negative" | "neutral" {
  if (n > 0) return "positive";
  if (n < 0) return "negative";
  return "neutral";
}
